import { AddRounded, EditRounded, ExpandMore } from '@mui/icons-material'
import { Box, ClickAwayListener, Grow, MenuItem, MenuList, Paper } from '@mui/material'
import clsx from 'clsx'
import { KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ButtonHeader from '../styles/ButtonHeader'
import { ButtonCategory, MenuItemCategory, StyledPopper, SvgSubMenu } from '../styles/TypeFilterStyles'

function TypeFilter(props: any) {
  const { t } = useTranslation(['global'])
  const { id, type, categories, active, handleChangeFilter, handleAction, admin, filterCategoryId } = props

  const [openMenu, setOpenMenu] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(openMenu)
  useEffect(() => {
    if (prevOpen.current && !openMenu) {
      anchorRef.current!.focus()
    }
    prevOpen.current = openMenu
  }, [openMenu])

  const handleItemSelected = (category_id: null | number) => {
    handleChangeFilter(id, category_id)
    setOpenMenu(false)
  }

  const handleToggle = () => {
    if (categories.length > 0) setOpenMenu(prevOpen => !prevOpen)
    else if (admin) setOpenMenu(prevOpen => !prevOpen)
    else handleItemSelected(null)
  }

  const handleClose = (event: MouseEvent<EventTarget>) => {
    if (anchorRef?.current?.contains(event.target as HTMLElement)) {
      return
    }
    setOpenMenu(false)
  }

  function handleListKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpenMenu(false)
    }
  }

  return (
    <>
      <ButtonHeader
        ref={anchorRef}
        aria-controls={openMenu ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        color="primary"
        className={`${clsx({ 'active ': active })} header-type`}
        endIcon={admin ? <ExpandMore color="inherit" /> : categories.length > 0 && <ExpandMore color="inherit" />}
        onClick={handleToggle}
      >
        {type}
      </ButtonHeader>
      {admin ? (
        <StyledPopper open={openMenu} anchorEl={anchorRef.current} transition disablePortal>
          {({ TransitionProps, placement }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom' }}>
              <Paper elevation={0} square>
                <ClickAwayListener onClickAway={handleClose as any}>
                  <MenuList autoFocusItem={openMenu} id="menu-list-grow" onKeyDown={handleListKeyDown}>
                    <MenuItem onClick={() => handleItemSelected(null)}>
                      <SvgSubMenu className={clsx(filterCategoryId === null && active && 'active', 'admin')} />
                      {t('all')}
                    </MenuItem>
                    {categories.map((category: any) => {
                      const activeCategory = filterCategoryId !== null && category.id === filterCategoryId

                      return (
                        <MenuItemCategory
                          key={category.id}
                          className={clsx(activeCategory && 'active', 'admin')}
                          onClick={() => handleItemSelected(category.id)}
                        >
                          <Box display="flex">
                            <SvgSubMenu className={clsx(activeCategory && 'active', 'admin')} />
                            {category.name}
                          </Box>
                          <ButtonCategory endIcon={<EditRounded color="inherit" />} onClick={() => handleAction('edit', category.id)} />
                        </MenuItemCategory>
                      )
                    })}
                    <MenuItemCategory>
                      {t('add')}
                      <ButtonCategory endIcon={<AddRounded color="inherit" />} onClick={() => handleAction('add', id)} />
                    </MenuItemCategory>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </StyledPopper>
      ) : (
        categories.length > 0 && (
          <StyledPopper open={openMenu} anchorEl={anchorRef.current} transition disablePortal>
            {({ TransitionProps, placement }) => (
              <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom' }}>
                <Paper elevation={0} square>
                  <ClickAwayListener onClickAway={handleClose as any}>
                    <MenuList autoFocusItem={openMenu} id="menu-list-grow" onKeyDown={handleListKeyDown}>
                      <MenuItemCategory
                        className={clsx(filterCategoryId === null && active && 'active')}
                        onClick={() => handleItemSelected(null)}
                      >
                        {t('all')}
                        <SvgSubMenu className={clsx(filterCategoryId === null && active && 'active')} />
                      </MenuItemCategory>
                      {categories.map((category: any) => {
                        const activeCategory = active && filterCategoryId !== null && category.id === filterCategoryId

                        return (
                          <MenuItemCategory
                            key={category.id}
                            className={clsx(activeCategory && 'active')}
                            onClick={() => handleItemSelected(category.id)}
                          >
                            {category.name}
                            <SvgSubMenu className={clsx(activeCategory && 'active')} />
                          </MenuItemCategory>
                        )
                      })}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </StyledPopper>
        )
      )}
    </>
  )
}

export default TypeFilter
